import { useEffect } from "react";
import { Link } from "react-router-dom";

const OG_IMAGE = "https://getblackride.com/logo.png";
const RELATED_LINKS = [
  {
    href: "/chauffeur-service-jeddah",
    title: "Chauffeur Service Jeddah",
    description: "A commercial landing page focused on premium city rides, executive transfers, and chauffeur service across Jeddah.",
  },
  {
    href: "/airport-transfer-jeddah",
    title: "Airport Transfer Jeddah",
    description: "A stronger fit for international arrivals who need premium airport pickup, hotel transfer, and reliable booking.",
  },
  {
    href: "/premium-chauffeur-service-hajj-umrah",
    title: "Premium Chauffeur Service for Hajj & Umrah",
    description: "Useful for religious travelers and families moving from Jeddah toward Makkah with more comfort and support.",
  },
  {
    href: "/uber-vs-chauffeur-service-saudi-arabia",
    title: "Uber vs Chauffeur Service in Saudi Arabia",
    description: "A practical comparison for travelers deciding between ride-hailing and a dedicated premium chauffeur service.",
  },
];

function ensureMeta(selector, identity, content) {
  let node = document.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    Object.entries(identity).forEach(([key, value]) => node.setAttribute(key, value));
    document.head.appendChild(node);
  }
  node.setAttribute("content", content);
}

export default function JeddahChauffeurService() {
  useEffect(() => {
    const title = "Chauffeur Service in Jeddah | Premium Airport & City Transfers | BlackRide";
    const description =
      "Book premium chauffeur service in Jeddah with BlackRide. Reliable airport transfers, business travel, private city rides, and luxury transport across Jeddah.";
    const canonical = "https://getblackride.com/jeddah-chauffeur-service";

    document.title = title;
    ensureMeta('meta[name="description"]', { name: "description" }, description);
    ensureMeta('meta[property="og:title"]', { property: "og:title" }, title);
    ensureMeta('meta[property="og:description"]', { property: "og:description" }, description);
    ensureMeta('meta[property="og:url"]', { property: "og:url" }, canonical);
    ensureMeta('meta[property="og:type"]', { property: "og:type" }, "article");
    ensureMeta('meta[property="og:image"]', { property: "og:image" }, OG_IMAGE);
    ensureMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image");
    ensureMeta('meta[name="twitter:title"]', { name: "twitter:title" }, title);
    ensureMeta('meta[name="twitter:description"]', { name: "twitter:description" }, description);
    ensureMeta('meta[name="twitter:image"]', { name: "twitter:image" }, OG_IMAGE);

    const canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) {
      canonicalTag.setAttribute("href", canonical);
    }

    const oldSchema = document.getElementById("service-schema");
    if (oldSchema) oldSchema.remove();

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "service-schema";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Chauffeur Service in Jeddah",
      description,
      provider: {
        "@type": "Organization",
        name: "BlackRide",
        url: "https://getblackride.com",
      },
      areaServed: {
        "@type": "City",
        name: "Jeddah",
      },
      serviceType: "Chauffeur Service",
      url: canonical,
    });
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white pt-24 md:pt-28">
      <section className="px-6 py-16 md:px-12 md:py-24 border-b border-white/10">
        <div className="max-w-6xl mx-auto grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-4">
              Premium Chauffeur Service
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
              Chauffeur Service in Jeddah
            </h1>
            <p className="text-gray-300 text-lg leading-8 mb-8 max-w-2xl">
              BlackRide offers premium chauffeur service in Jeddah for airport transfers,
              executive travel, private city rides, hotel pickups, and family transportation.
              If you want a smoother, more reliable, and more professional way to travel in Jeddah,
              our chauffeur-driven vehicles are designed for comfort, punctuality, and peace of mind.
            </p>

            <div className="flex gap-4 flex-wrap">
              <a
                href="https://getblackride.com"
                className="inline-block bg-[#C7A239] text-black px-6 py-3 rounded-full font-medium hover:opacity-90 transition"
              >
                Book Chauffeur Service
              </a>
              <Link
                to="/blog"
                className="inline-block border border-white/20 text-white px-6 py-3 rounded-full font-medium hover:bg-white hover:text-black transition"
              >
                Read Travel Guides
              </Link>
            </div>
          </div>

          <div className="bg-[#111] border border-white/10 rounded-2xl p-6">
            <h2 className="text-2xl font-semibold mb-4">Why travelers choose BlackRide in Jeddah</h2>
            <ul className="space-y-4 text-gray-300 leading-7">
              <li>Reliable airport pickup and drop-off in Jeddah</li>
              <li>Professional chauffeur service for business and VIP travel</li>
              <li>Luxury sedans, SUVs, and premium transfer options</li>
              <li>Comfortable rides for families, executives, and visitors</li>
              <li>Private city travel with more convenience and less stress</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="px-6 py-16 md:px-12">
        <div className="max-w-5xl mx-auto space-y-10 text-gray-300 leading-8">
          <div>
            <h2 className="text-3xl font-semibold text-white mb-4">
              Premium chauffeur booking in Jeddah
            </h2>
            <p>
              Finding dependable transportation in a busy city can be difficult when comfort,
              punctuality, and presentation all matter. That is why many travelers now prefer
              chauffeur booking in Jeddah instead of relying only on ordinary ride options.
              Whether you are arriving at the airport, attending a business meeting, going to
              a hotel, or arranging premium transport for family members, chauffeur service offers
              a more refined and predictable experience.
            </p>
            <p>
              At BlackRide, we focus on making every ride feel smooth from the beginning.
              Our service is designed for travelers who do not want uncertainty around pickup,
              vehicle quality, or professionalism. Instead, they want dependable transport
              that matches the importance of the trip.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-semibold text-white mb-4">
              Airport transfers in Jeddah with more comfort
            </h2>
            <p>
              One of the most common reasons travelers book chauffeur service in Jeddah is airport
              transportation. After a long flight, most people want a direct, well-organized, and
              comfortable transfer. A pre-booked chauffeur service helps reduce uncertainty and
              makes the arrival experience much easier.
            </p>
            <p>
              This is especially helpful for executives, international visitors, families with
              luggage, and travelers on important schedules. Instead of trying to solve transport
              after landing, you can move directly toward your destination with more confidence.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-semibold text-white mb-4">
              Chauffeur service for business travel in Jeddah
            </h2>
            <p>
              Business travel requires punctuality, privacy, and strong presentation. If you are
              hosting a guest, attending an important meeting, or arranging executive transportation,
              the quality of the ride matters. A professional chauffeur service supports a better
              business travel experience by helping travelers stay on schedule and travel in comfort.
            </p>
            <p>
              It also creates a more polished impression for corporate visitors and VIP clients.
              In many situations, premium transportation is not just a luxury choice. It is the
              practical choice for serious business travel.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-semibold text-white mb-4">
              Private luxury rides across Jeddah
            </h2>
            <p>
              Many customers also choose chauffeur service for private city travel in Jeddah.
              This can include hotel transfers, shopping trips, family rides, premium evening
              transportation, and travel across the city with greater comfort and privacy.
            </p>
            <p>
              Compared with ordinary ride options, private luxury transport gives passengers
              a more consistent and premium experience. That makes it a better fit for travelers
              who value comfort, convenience, and service quality.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-semibold text-white mb-4">
              Why BlackRide is a strong choice in Jeddah
            </h2>
            <p>
              BlackRide is built around the needs of travelers who want premium service in Saudi Arabia.
              Our goal is to make transportation feel reliable, clean, and professionally managed from
              booking to drop-off. Whether you need a ride from the airport, executive transport, or
              private luxury travel in Jeddah, we aim to deliver a smoother experience throughout the trip.
            </p>
          </div>
        </div>
      </section>

      <section className="px-6 py-16 md:px-12 bg-[#0b0b0b] border-y border-white/10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-semibold text-white mb-8">Frequently Asked Questions</h2>

          <div className="space-y-6 text-gray-300 leading-8">
            <div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Can I book chauffeur service in Jeddah for airport transfers?
              </h3>
              <p>
                Yes. BlackRide offers premium airport transfer service in Jeddah for individuals,
                families, and business travelers who want reliable pickup and drop-off.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Is chauffeur service in Jeddah suitable for business travel?
              </h3>
              <p>
                Yes. Many professionals use chauffeur service for executive meetings, VIP guests,
                corporate transport, and more punctual travel across the city.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Do you offer private luxury rides in Jeddah?
              </h3>
              <p>
                Yes. BlackRide provides private and premium transportation options for city travel,
                hotel transfers, family rides, and special travel needs.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-2">
                How do I book chauffeur service in Jeddah?
              </h3>
              <p>
                You can book directly through{" "}
                <a href="https://getblackride.com" className="text-[#C7A239] hover:underline">
                  getblackride.com
                </a>{" "}
                for a more convenient and professional booking experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-16 md:px-12 border-b border-white/10">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-3">Explore More</p>
              <h2 className="text-3xl font-semibold text-white">Related BlackRide pages</h2>
            </div>
            <Link to="/blog" className="text-[#C7A239] hover:text-[#D4B34A] transition font-medium">
              Explore all BlackRide guides
            </Link>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            {RELATED_LINKS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="rounded-2xl border border-white/10 bg-[#111] p-6 hover:border-[#C7A239]/40 hover:-translate-y-0.5 transition-all"
              >
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-gray-300 leading-7">{item.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 md:px-12">
        <div className="max-w-5xl mx-auto rounded-2xl bg-[#111] border border-white/10 p-8 md:p-10">
          <h2 className="text-3xl font-semibold text-white mb-4">
            Book your chauffeur service in Jeddah
          </h2>
          <p className="text-gray-300 leading-8 mb-6 max-w-3xl">
            If you are looking for premium chauffeur booking in Jeddah, airport transfers,
            executive rides, or private luxury transportation, BlackRide is ready to help you travel
            with more comfort and confidence.
          </p>

          <div className="flex gap-4 flex-wrap">
            <a
              href="https://getblackride.com"
              className="inline-block bg-[#C7A239] text-black px-6 py-3 rounded-full font-medium hover:opacity-90 transition"
            >
              Book Now on BlackRide
            </a>
            <Link
              to="/blog/jeddah-airport-transfer-guide"
              className="inline-block border border-white/20 text-white px-6 py-3 rounded-full font-medium hover:bg-white hover:text-black transition"
            >
              Read Jeddah Airport Guide
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
